import type {ControlledTooltipProps} from '@github-ui/portal-tooltip/controlled'
import type {PortalTooltipProps} from '@github-ui/portal-tooltip/portalled'
import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {useRef, type CSSProperties, type ReactNode} from 'react'

type TooltipDirection = NonNullable<ControlledTooltipProps['direction']>

interface NonInteractiveTooltipProps {
  /** Text shown inside the tooltip. */
  label: string
  direction?: TooltipDirection
  children: ReactNode
  className?: string
  style?: CSSProperties
  role?: string
  'aria-label'?: string
  'data-testid'?: string
  /**
   * When `false`, the anchor is not given an `aria-describedby` pointing at the tooltip. Use this
   * when the anchor already exposes the same text through its own `aria-label` to avoid the label
   * being announced twice.
   */
  includeAriaAttributes?: boolean
}

/**
 * Maps a tooltip `direction` to anchored-position settings. `direction` only controls where the
 * tooltip bubble sits relative to its (zero-size) anchor point via CSS, so the anchor point itself
 * must be positioned on the matching side of the trigger — otherwise the bubble renders over the
 * trigger instead of beside it.
 */
const DIRECTION_TO_ANCHOR: Record<
  TooltipDirection,
  {
    anchorSide: PortalTooltipProps['anchorSide']
    anchoredPositionAlignment: PortalTooltipProps['anchoredPositionAlignment']
  }
> = {
  n: {anchorSide: 'outside-top', anchoredPositionAlignment: 'center'},
  s: {anchorSide: 'outside-bottom', anchoredPositionAlignment: 'center'},
  e: {anchorSide: 'outside-right', anchoredPositionAlignment: 'center'},
  w: {anchorSide: 'outside-left', anchoredPositionAlignment: 'center'},
  ne: {anchorSide: 'outside-top', anchoredPositionAlignment: 'end'},
  nw: {anchorSide: 'outside-top', anchoredPositionAlignment: 'start'},
  se: {anchorSide: 'outside-bottom', anchoredPositionAlignment: 'end'},
  sw: {anchorSide: 'outside-bottom', anchoredPositionAlignment: 'start'},
}

const anchorBaseStyle: CSSProperties = {display: 'inline-flex', alignItems: 'center'}

/**
 * Wraps non-interactive content (a status icon, badge, or similar) with a tooltip that is portalled
 * to the document body. Portalling lets the tooltip escape the `overflow: hidden` used by ancestors
 * for text truncation, so the tooltip is not clipped by its container. This also fills the gap left
 * by the modern `@primer/react` Tooltip, which requires an interactive trigger.
 *
 * Motivating case: review-count tooltips on the pull-request dashboard lists, whose list container
 * and list-item content areas both rely on `overflow: hidden`.
 */
export function NonInteractiveTooltip({
  label,
  direction = 's',
  children,
  style,
  includeAriaAttributes = true,
  ...anchorProps
}: NonInteractiveTooltipProps) {
  const contentRef = useRef<HTMLSpanElement>(null)
  const {anchorSide, anchoredPositionAlignment} = DIRECTION_TO_ANCHOR[direction]
  const [triggerProps, tooltipElement] = usePortalTooltip({
    contentRef,
    'aria-label': label,
    direction,
    anchorSide,
    anchoredPositionAlignment,
    includeAriaAttributes,
  })

  return (
    <span ref={contentRef} style={{...anchorBaseStyle, ...style}} {...anchorProps} {...triggerProps}>
      {children}
      {tooltipElement}
    </span>
  )
}
